import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { FormsModule } from '@angular/forms';
import { JobService } from '../../../services/job.service';
import { JobDetailCardComponent, JobDetailCardData } from '../../../components/job-detail-card/job-detail-card.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterModule } from '@angular/router';
import { LanguageRoutingService } from '../../../services/language-routing.service';
import { RecommendedJob } from '../../../models/job.model';
import { LoadMoreButtonComponent } from '../../../components/load-more-button/load-more-button.component';

// Extended interface with pre-computed localized route
interface AppliedJobCardData extends JobDetailCardData {
  localizedRoute: string;
}

@Component({
  selector: 'app-applied-jobs',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    FormsModule,
    JobDetailCardComponent,
    MatProgressSpinnerModule,
    RouterModule,
    LoadMoreButtonComponent
  ],
  templateUrl: './applied-jobs.component.html',
  styleUrls: ['./applied-jobs.component.scss']
})
export class AppliedJobsComponent implements OnInit {
  appliedJobs: RecommendedJob[] = [];
  mappedJobs: AppliedJobCardData[] = [];
  totalApplications: number = 0;
  remainingCount: number = 0;
  hasMoreJobs: boolean = false;
  currentPage: number = 0;
  isLoading = true;
  isLoadingMore = false;
  error: string | null = null;
  selectedLimit: number = 10;
  limitOptions = [
    { value: 5, label: '5' },
    { value: 10, label: '10' },
    { value: 15, label: '15' },
    { value: 20, label: '20' },
    { value: 25, label: '25' },
    { value: 30, label: '30' }
  ];

  constructor(
    private jobService: JobService,
    private languageRoutingService: LanguageRoutingService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    this.loadAppliedJobs();
  }

  loadAppliedJobs(): void {
    this.isLoading = true;
    this.error = null;
    this.currentPage = 0;

    this.jobService.getAppliedJobs(this.selectedLimit, this.currentPage).subscribe({
      next: (response) => {
        this.appliedJobs = response.jobs;
        this.totalApplications = response.totalElements;
        this.mappedJobs = this.mapAppliedJobsToCardData(response.jobs);
        this.updatePaginationState();
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading applied jobs:', err);
        this.error = this.translocoService.translate('pages.jobs.appliedJobs.loadError');
        this.isLoading = false;
      }
    });
  }

  loadMore(): void {
    if (this.isLoadingMore || !this.hasMoreJobs) {
      return;
    }

    this.isLoadingMore = true;
    this.currentPage++;

    this.jobService.getAppliedJobs(this.selectedLimit, this.currentPage).subscribe({
      next: (response) => {
        this.appliedJobs = [...this.appliedJobs, ...response.jobs];
        const newMappedJobs = this.mapAppliedJobsToCardData(response.jobs);
        this.mappedJobs = [...this.mappedJobs, ...newMappedJobs];
        this.updatePaginationState();
        this.isLoadingMore = false;
      },
      error: (err) => {
        console.error('Error loading more applied jobs:', err);
        this.error = this.translocoService.translate('pages.jobs.appliedJobs.loadMoreError');
        this.isLoadingMore = false;
      }
    });
  }

  private updatePaginationState(): void {
    const remaining = this.totalApplications - this.mappedJobs.length;
    this.remainingCount = Math.min(remaining, this.selectedLimit);
    this.hasMoreJobs = this.mappedJobs.length < this.totalApplications;
  }

  onLimitChange(newLimit: number): void {
    this.selectedLimit = newLimit;
    this.loadAppliedJobs();
  }

  private mapAppliedJobsToCardData(jobs: RecommendedJob[]): AppliedJobCardData[] {
    return jobs.map(job => {
      const mappedJob: AppliedJobCardData = {
        ...job,
        location: job.placeOfWork,
        // Applied jobs specific fields are already on the RecommendedJob object
        applicationStatus: (job as any).applicationStatus ? { statusName: (job as any).applicationStatus } : undefined,
        appliedDate: (job as any).appliedDate,
        assignedBy: undefined,
        // Pre-compute the localized route to avoid function calls in template
        localizedRoute: this.languageRoutingService.getLocalizedRoute('/job/' + job.id)
      };

      return mappedJob;
    });
  }

  getLocalizedRoute(path: string): string {
    return this.languageRoutingService.getLocalizedRoute(path);
  }

  // Handle save/unsave actions - not used for applied jobs but required by interface
  onSaveJob(jobId: string): void {
    console.log('Save job action not applicable for applied jobs:', jobId);
  }

  onUnsaveJob(jobId: string): void {
    console.log('Unsave job action not applicable for applied jobs:', jobId);
  }
}
